import { api } from '../client';
import type { SettingCatalogueEntry } from '../types.settings';

/** Effective tenant settings: a flat key→value map plus a grouped view. */
export interface SettingsResponse {
  values: Record<string, unknown>;
  groups: Record<string, Record<string, unknown>>;
}

/** The `branding` group of a settings response — see setting.catalogue.js. */
export interface BrandingSettings {
  companyName: string;
  logoUrl: string;
  faviconUrl: string;
  primaryColor: string;
  accentColor: string;
  loginTagline: string;
  hidePoweredBy: boolean;
}

/** GET /settings/public/:slug — unauthenticated, white-label branding only. */
export interface PublicBrandingResponse {
  tenant: { name: string; slug: string };
  values: Record<string, unknown>;
  groups: { branding: BrandingSettings };
}

export const settingsService = {
  get: (): Promise<SettingsResponse> => api.get('/api/v1/settings'),
  update: (patch: Record<string, unknown>): Promise<SettingsResponse> => api.patch('/api/v1/settings', patch),
  /** UI descriptor for every configurable key (group, type, options, help text). */
  catalogue: (): Promise<SettingCatalogueEntry[]> => api.get('/api/v1/settings/catalogue'),
  /** Reset the given keys to their catalogue defaults (1–50 keys). */
  reset: (keys: string[]): Promise<SettingsResponse> => api.post('/api/v1/settings/reset', { keys }),
  /** Pre-auth white-label branding lookup by tenant slug — no bearer token sent. */
  publicBranding: (slug: string): Promise<PublicBrandingResponse> =>
    api.get(`/api/v1/settings/public/${encodeURIComponent(slug)}`, undefined, { authRequired: false }),
  /**
   * Same, but resolved from the Host header — for a visitor on a tenant's
   * custom domain who doesn't know (and on a white-labeled domain, shouldn't
   * need to know) their workspace slug. `null` on the platform's own domain.
   */
  publicBrandingByHost: (): Promise<PublicBrandingResponse | null> =>
    api.get('/api/v1/settings/public', undefined, { authRequired: false }),
};
